import { getBoxSize } from "../../utils";
import RenderImage from "./RenderImage";
import RenderString from "./RenderString";
import React, { useEffect, useMemo } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import RenderMonth from "./RenderMonth";
import {
  ACTIONS,
  getDataByPartRef,
  getFirstRenderPartByResource,
  getPartRefById,
  getResource,
} from "../../Providers/TemplateInstanceProvider";
import CropImage from "./CropImage";
import { getRotatedBoundingRect } from "./stringutils";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  template: {
    backgroundColor: "white",
    position: "absolute",
    boxSizing: "border-box",
  },
  templateTrimBox: {
    overflow: "hidden",
    position: "absolute",
  },
});

const DrawTemplate = ({
  templateInstance,
  containerSize,
  partRefId,
  year,
  zoomResource,
  handleUserImageClick,
  handleUserStringClick,
  handleTextInputFocus,
  handleTextInputBlur,
  handleTextInputChange,
  activeTextInputRef,
  handleCropUpdate = null,
  handleCropComplete,
  dispatch,
  outerPosition,
  userImageUrlCreator,
  mode,
}) => {
  const classes = useStyles();

  year = year || templateInstance.year;

  const partRef = useMemo(() => {
    return getPartRefById(
      partRefId || templateInstance.currentPartRefId,
      templateInstance.template,
    );
  }, [templateInstance]);

  const templateData = useMemo(() => {
    return getDataByPartRef(partRef, templateInstance);
  }, [partRef, templateInstance]);

  const dimensions = templateData.dimensions;
  // We can fit template dimensions or zoomResource:
  let [scaleRatio, templateScaleRatio, zp, transformScale] = useMemo(() => {
    let visibleArea = dimensions;
    console.log("Template and instance", templateInstance, templateData);
    if (templateInstance.template.trimBox) {
      visibleArea = templateInstance.template.trimBox;
    }
    let sizeInfo = getBoxSize("fit-in", visibleArea, {
      width: containerSize.width,
      height: containerSize.height,
    });

    let scaleRatio = (templateScaleRatio = sizeInfo.width / visibleArea.width);

    let zp = { x: 0, y: 0 };

    let transformScale = 1;

    if (zoomResource) {
      resourceRenderElement = getFirstRenderPartByResource(
        zoomResource,
        templateInstance.template,
      );
      let resourceDimensions = {
        width: resourceRenderElement.size.width,
        height: resourceRenderElement.size.height,
      };

      const rotation = resourceRenderElement.transformations.find(
        (t) => t.type == "ROTATE",
      );

      let position = resourceRenderElement.position;
      if (rotation) {
        const boundingRect = getRotatedBoundingRect(
          resourceRenderElement.position,
          resourceRenderElement.size,
          rotation.rotate,
          { x: rotation.originX, y: rotation.originY },
        );

        resourceDimensions = {
          width: boundingRect.width,
          height: boundingRect.height,
        };

        position = {
          x: boundingRect.x,
          y: boundingRect.y,
        };
      }

      let info = getBoxSize("fit-in", resourceDimensions, outerPosition);

      transformScale = info.height / (resourceDimensions.height * scaleRatio);

      zp = {
        x:
          -(position.x * scaleRatio * transformScale) -
          (outerPosition.x - info.x),
        y:
          -(position.y * scaleRatio * transformScale) -
          (outerPosition.y - info.y),
      };
    }

    return [scaleRatio, templateScaleRatio, zp, transformScale];
  }, [containerSize, zoomResource]);

  useEffect(() => {
    if (dispatch) {
      dispatch({
        type: ACTIONS.SET_SCALE_RATIO,
        scaleRatio,
      });
    }
  }, [scaleRatio]);

  const transform = `translate3D(${zp.x}px, ${zp.y}px, 0)  scale(${transformScale}) `;
  console.log("Transform the stuff", transform);

  const styles = useMemo(() => {
    return {
      width: dimensions.width * scaleRatio + "px",
      height: dimensions.height * scaleRatio + "px",
      transformOrigin: "0 0 0",
      transform,
    };
  }, [zp, dimensions]);

  let resourceRenderElement = null;

  let trimBoxStyles = {
    width: dimensions.width * scaleRatio + "px",
    height: dimensions.height * scaleRatio + "px",
  };
  if (templateInstance.template.trimBox) {
    const trimBox = templateInstance.template.trimBox;
    trimBoxStyles = {
      // FIXME: Top and left probably does not work for displaycategory.php, need  a fix for it.
      left: -(trimBox.x * scaleRatio) + "px",
      top: -(trimBox.y * scaleRatio) + "px",
      width: trimBox.width * scaleRatio + "px",
      height: trimBox.height * scaleRatio + "px",
    };
  }

  return (
    <div
      style={trimBoxStyles}
      className={`${classes.templateTrimBox} template-inner-container`}
    >
      <div style={styles} className={`${classes.template} `}>
        <RenderParts
          templateData={templateData}
          scaleRatio={scaleRatio}
          templateInstance={templateInstance}
          handleTextInputFocus={handleTextInputFocus}
          zoomResource={zoomResource}
          handleTextInputBlur={handleTextInputBlur}
          handleTextInputChange={handleTextInputChange}
          handleUserStringClick={handleUserStringClick}
          handleUserImageClick={handleUserImageClick}
          activeTextInputRef={activeTextInputRef}
          year={year}
          mode={mode}
          userImageUrlCreator={userImageUrlCreator}
        />

        {/* Overlay */}
        <Overlays
          templateData={templateData}
          templateInstance={templateInstance}
          handleUserImageClick={handleUserImageClick}
          handleUserStringClick={handleUserStringClick}
          scaleRatio={scaleRatio}
        />

        {handleCropUpdate && zoomResource && !zoomResource.disableZoom && (
          <CropImage
            resource={zoomResource}
            templateInstance={templateInstance}
            handleCropUpdate={handleCropUpdate}
            handleCropComplete={handleCropComplete}
            scaleRatio={scaleRatio}
            template={templateInstance.template}
          />
        )}
      </div>
    </div>
  );
};

const RenderParts = (props) => {
  let userResourceCount = 0;
  return props.templateData.renderParts.map((renderPart) => {
    const key = props.templateData.partRef.id + "#" + renderPart.resourceId;
    const { zoomResource, mode, ...rest } = props;
    const partZoomResource =
      zoomResource && zoomResource.id == renderPart.resourceId
        ? zoomResource
        : null;
    const zoomResourceId = zoomResource && zoomResource.fullId;

    // Heavy operation, only do this if we need:
    if (mode == "select-user-image" || mode == "select-user-text") {
      const resource = getResource(
        props.templateData.partRef,
        renderPart.resourceId,
        props.templateInstance,
      );
      if (resource?.userSupplied && !resource?.disableEdit) {
        if (mode == "select-user-image" && resource.type == "IMAGE") {
          userResourceCount++;
        }
        if (mode == "select-user-text" && resource.type == "STRING") {
          userResourceCount++;
        }
      }
    }

    return (
      <RenderPart
        key={key}
        renderPart={renderPart}
        zoomResource={partZoomResource}
        zoomResourceId={zoomResourceId}
        mode={mode}
        resourceNumber={userResourceCount}
        {...rest}
      />
    );
  });
};

const RenderPart = React.memo(
  ({
    templateData,
    templateInstance,
    scaleRatio,
    zoomResource,
    handleUserImageClick,
    handleUserStringClick,
    handleTextInputFocus,
    handleTextInputChange,
    handleTextInputBlur,
    activeTextInputRef,
    zoomResourceId,
    year,
    renderPart,
    mode,
    resourceNumber,
    userImageUrlCreator,
  }) => {
    const resource = useMemo(() => {
      return zoomResource?.id == renderPart.resourceId
        ? zoomResource
        : getResource(
            templateData.partRef,
            renderPart.resourceId,
            templateInstance,
          );
    }, [
      templateData.partRef,
      renderPart.resourceId,
      templateInstance,
      zoomResource,
    ]);

    switch (renderPart.type) {
      case "IMAGE":
        return (
          <RenderImage
            renderPart={renderPart}
            resource={resource}
            handleUserImageClick={handleUserImageClick}
            zoomResource={zoomResource}
            scaleRatio={scaleRatio}
            mode={mode}
            zoomResourceId={zoomResourceId}
            resourceNumber={resourceNumber}
            templateInstance={templateInstance}
            userImageUrlCreator={userImageUrlCreator}
          />
        );

      case "STRING":
        return (
          <RenderString
            renderPart={renderPart}
            resource={resource}
            monthStrings={templateData.partRef.months}
            handleTextInputFocus={handleTextInputFocus}
            handleTextInputBlur={handleTextInputBlur}
            handleTextInputChange={handleTextInputChange}
            handleUserStringClick={handleUserStringClick}
            zoomResource={zoomResource}
            scaleRatio={scaleRatio}
            year={year}
            mode={mode}
            activeTextInputRef={
              resource?.fullId == zoomResourceId && activeTextInputRef
            }
            zoomResourceId={zoomResourceId}
            resourceNumber={resourceNumber}
          />
        );
      case "MONTH":
        return (
          <RenderMonth
            renderPart={renderPart}
            resource={resource}
            monthStrings={templateData.partRef.months}
            scaleRatio={scaleRatio}
            year={year}
          />
        );
    }

    return null;
  },
);

const Overlays = React.memo(
  ({
    templateData,
    templateInstance,
    handleUserImageClick,
    handleUserStringClick,
    scaleRatio,
  }) => {
    const filteredRenderParts = templateData.renderParts.filter(
      (renderPart) => {
        const resource = getResource(
          templateData.partRef,
          renderPart.resourceId,
          templateInstance,
        );
        console.log("Overlay resource", renderPart.resourceId, resource);
        if (!resource) {
          return false;
        }
        if (resource.type == "IMAGE" && !handleUserImageClick) {
          return false;
        }
        if (resource.type == "STRING" && !handleUserStringClick) {
          return false;
        }
        if (resource.disableEdit) {
          return false;
        }
        return resource.userSupplied;
      },
    );
    let currentImageIndex = -1;
    let currentStringIndex = -1;
    let currentResoureIndex = -1;
    const userResourceOverslays = [];
    for (const renderPart of filteredRenderParts) {
      const key = templateData.partRef.id + "#" + renderPart.resourceId;
      const resource = getResource(
        templateData.partRef,
        renderPart.resourceId,
        templateInstance,
      );

      if (resource.type == "IMAGE") {
        currentImageIndex++;
        currentResoureIndex = currentImageIndex;
      } else {
        currentStringIndex++;
        currentResoureIndex = currentStringIndex;
      }

      const clickHandler =
        resource.type == "IMAGE"
          ? handleUserImageClick
          : handleUserStringClick;
      userResourceOverslays.push(
        <UserResourceOverlay
          key={key}
          resourceIndex={currentResoureIndex}
          resource={resource}
          renderPart={renderPart}
          onClick={clickHandler}
          scaleRatio={scaleRatio}
        />,
      );
    }
    return userResourceOverslays;
  },
);

const UserResourceOverlay = ({
  resource,
  renderPart,
  resourceIndex,
  onClick,
  scaleRatio,
}) => {
  const [t] = useTranslation();

  let rotate = 0;
  let rotateOriginX = 0;
  let rotateOriginY = 0;

  const scaledRenderPart = useMemo(() => {
    return {
      width: renderPart.size.width * scaleRatio,
      height: renderPart.size.height * scaleRatio,
      x: renderPart.position.x * scaleRatio,
      y: renderPart.position.y * scaleRatio,
    };
  }, [renderPart, scaleRatio]);

  for (const transformation of renderPart.transformations) {
    if (transformation.type == "ROTATE") {
      rotate = transformation.rotate;
      rotateOriginY = parseInt(transformation.originY * scaleRatio);
      rotateOriginX = parseInt(transformation.originX * scaleRatio);
    }
  }

  const styles = {
    position: "absolute",
    boxSizing: "border-box",
    width: scaledRenderPart.width + "px",
    height: scaledRenderPart.height + "px",
    top: scaledRenderPart.y + "px",
    left: scaledRenderPart.x + "px",
    backgroundColor: "transparent",
    border: "none",
    transformOrigin: `${rotateOriginX}px ${rotateOriginY}px`,
  };

  if (rotate) {
    styles.transform = `rotate(-${rotate}deg)`;
  }
  return (
    <button
      style={styles}
      aria-label={t(
        resource.type === "IMAGE" ? "Edit image %d" : "Edit text %d",
        {
          postProcess: "sprintf",
          sprintf: [resourceIndex + 1],
        },
      )}
      onClick={(e) => onClick(e, resource)}
    />
  );
};

export default DrawTemplate;
